/**
 * Genesys Cloud browser auth — server-side PKCE trigger via a POPUP (#343 + #422).
 *
 * The Genesys Authorization-Code + PKCE token exchange runs on the BFF, and the
 * resulting access token is held in the server-side session. The browser NEVER
 * holds a Genesys token. This module only TRIGGERS the round-trip.
 *
 * WHY A POPUP (not a hidden iframe): the app is embedded in the Genesys **script**
 * web-page component, so a hidden iframe to `login.<region>` is a THIRD-PARTY
 * context. Browsers block third-party cookies there, so:
 *   1. `prompt=none` cannot see the agent's Genesys session → no code, and
 *   2. even when a code came back, the BFF **callback** (also in the 3p iframe)
 *      did not receive the app's session cookie, so the PKCE `code_verifier`
 *      could not be matched and the token landed in no session
 *      (`pinned-token-not-linked` forever — confirmed on staging/PreProd).
 * A popup runs BOTH the Genesys login AND our `/auth/genesys/callback` as
 * FIRST-PARTY top-level navigations, so both the Genesys login cookie and our
 * app session cookie are sent → PKCE completes → the token lands in the agent's
 * session. This requires a user gesture (browsers block gesture-less popups), so
 * the caller invokes {@link linkGenesysViaPopup} from a click.
 *
 * Completion is detected by POLLING `/api/session` (`genesysLinked`) rather than
 * relying on `postMessage`: `Cross-Origin-Opener-Policy` can sever `window.opener`
 * once the popup navigates cross-origin to the IdP, so the postMessage may never
 * arrive — but the token still lands in the server session, which the poll sees.
 * A `postMessage` from the callback page, when it does arrive, only accelerates
 * the poll.
 */

import { fetchSession } from '../session.js';

/** postMessage type the BFF callback page sends on completion (accelerator only). */
export const GENESYS_AUTH_MESSAGE = 'genesys-auth-complete';
const POPUP_NAME = 'genesys-link';
const POLL_INTERVAL_MS = 1000;
const LINK_TIMEOUT_MS = 120_000;

/** In-flight guard so a double-click cannot open two concurrent popups. */
let linkInFlight = false;

/** Asks the BFF for the authorize URL (PKCE state stays server-side). */
async function fetchAuthorizeUrl(): Promise<string | undefined> {
  try {
    const res = await fetch('/api/genesys/authorize-url', {
      credentials: 'include',
    });
    if (!res.ok) return undefined;
    const json = (await res.json()) as { authorizeUrl?: string };
    return json.authorizeUrl;
  } catch {
    return undefined;
  }
}

/** Re-reads `/api/session` and reports whether the BFF now holds a token. */
async function isLinked(): Promise<boolean> {
  try {
    return (await fetchSession()).genesysLinked === true;
  } catch {
    return false;
  }
}

/**
 * Opens the Genesys authorize URL in a popup and resolves `true` once the BFF
 * session holds a token (polled), or `false` on timeout / blocked popup / the
 * user closing the window before it completed.
 *
 * MUST be called from a user gesture (click) — otherwise the popup is blocked.
 */
export async function linkGenesysViaPopup(): Promise<boolean> {
  if (linkInFlight) return false;
  linkInFlight = true;
  try {
    if (await isLinked()) return true;

    const authorizeUrl = await fetchAuthorizeUrl();
    if (!authorizeUrl) return false;

    const popup = window.open(
      authorizeUrl,
      POPUP_NAME,
      'popup,width=480,height=680,noopener=no',
    );
    // Popup blocked (no handle) — the caller surfaces a retry affordance.
    if (!popup) return false;

    // A postMessage from the callback page (if COOP left the opener intact) just
    // wakes the poll early; the poll below is the authoritative signal.
    let nudged = false;
    const onMessage = (e: MessageEvent): void => {
      if (e.origin !== window.location.origin) return;
      const data = e.data as { type?: string } | null;
      if (data?.type === GENESYS_AUTH_MESSAGE) nudged = true;
    };
    window.addEventListener('message', onMessage);

    try {
      const deadline = Date.now() + LINK_TIMEOUT_MS;
      // Poll until the server session shows a token, the popup is closed, or we
      // time out. `postMessage` (nudged) collapses the wait to the next tick.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, nudged ? 0 : POLL_INTERVAL_MS));
        if (await isLinked()) {
          try {
            popup.close();
          } catch {
            /* cross-origin/COOP — the popup closes itself on the done page */
          }
          return true;
        }
        let closed = false;
        try {
          closed = popup.closed;
        } catch {
          /* COOP severed the handle — keep polling until the deadline */
        }
        if (closed) return isLinked();
        if (Date.now() > deadline) return false;
      }
    } finally {
      window.removeEventListener('message', onMessage);
    }
  } finally {
    linkInFlight = false;
  }
}

/**
 * Back-compat shim: previously the app auto-triggered a silent link on mount.
 * The link now requires a user gesture (popup), so a gesture-less call can only
 * report the CURRENT state — it never opens a popup. Returns `true` when already
 * linked, else `false` (the caller then renders the Connect affordance which
 * calls {@link linkGenesysViaPopup} on click).
 */
export async function ensureGenesysLink(
  alreadyLinked: boolean,
): Promise<boolean> {
  return alreadyLinked ? true : isLinked();
}
